/** Facilities - a place of ADR-0212's menu (Records): Sites, Companies and Structure.
 *
 * Three tabs, each a title card, a strip of tiles, one filter line and one table:
 *
 * - **Sites** - where the energy is used, with lease treatment, coverage and state; a row opens the
 *   site's own page (`facilities/SitesTab.tsx`);
 * - **Companies** - the legal entities that file, their parents and their filing windows
 *   (`facilities/CompaniesTab.tsx`);
 * - **Structure** - the organisation as a tree with a total on every node, read-only
 *   (`facilities/StructureTab.tsx`).
 *
 * Everyone reads; **changing the shape of the company is an Admin's act** (it changes what every
 * figure is about), so Add, Register many from a file, the corrections, lease treatment, ownership
 * and filing windows are drawn for an Admin only, in drawers holding the forms the Organisation
 * screen has always had. `/organisation` redirects here; a site's page and the structure file keep
 * their addresses.
 */

import { useOrgTree } from "../api/vendorSummary";
import { RequireSession } from "../shell/RequireSession";
import { CompaniesTab } from "./facilities/CompaniesTab";
import { SitesTab } from "./facilities/SitesTab";
import { StructureTab } from "./facilities/StructureTab";
import { ScreenFrame } from "./ScreenFrame";
import { useNodeBreakdown } from "./tableKit";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";

const TABS = [
  { key: "sites", label: "Sites" },
  { key: "companies", label: "Companies" },
  { key: "structure", label: "Structure" },
] as const;

function FacilitiesBody() {
  const screen = useScreen();
  const [tab] = useUrlTab(TABS);
  // A node's tonnage - a site's, a company's, the whole organisation's, by scope or in all - opens the
  // figures it is the sum of (rule 3). The cut and the headline are the tree's own.
  const tree = useOrgTree(screen.window?.period_start, screen.window?.period_end, screen.method);
  const breakdown = useNodeBreakdown(screen, (key) => {
    const match = /^(site|company|all)(?:-([0-9a-f-]{36}))?(?:-s([123]))?$/.exec(key);
    const data = tree.data;
    if (!match || !data) return undefined;
    const [, kind, id, scope] = match;
    const node =
      kind === "site"
        ? data.facilities.find((f) => f.facility_id === id)
        : kind === "company"
          ? data.entities.find((e) => e.entity_id === id)
          : { totals: data.workspace, name: "", legal_name: "", facility_ids: undefined };
    if (!node) return undefined;
    const totals = node.totals;
    const name = kind === "site" ? (node as { name: string }).name : kind === "company" ? (node as { legal_name: string }).legal_name : "Whole organisation";
    const ids =
      kind === "site" && id ? [id] : kind === "company" ? (node as { facility_ids: string[] }).facility_ids : undefined;
    return {
      label: scope ? `${name}, Scope ${scope}` : name,
      cut: { ...(ids ? { facilityIds: ids } : {}), ...(scope ? { scope: scope as "1" | "2" | "3" } : {}) },
      headline: scope ? totals.by_scope[Number(scope) - 1]?.kgco2e : totals.kgco2e,
    };
  });
  return (
    <ScreenFrame screen={screen} title="Facilities" {...breakdown}>
      {tab === "companies" ? (
        <CompaniesTab screen={screen} />
      ) : tab === "structure" ? (
        <StructureTab screen={screen} />
      ) : (
        <SitesTab screen={screen} />
      )}
    </ScreenFrame>
  );
}

export function FacilitiesScreen() {
  return (
    <RequireSession>
      <FacilitiesBody />
    </RequireSession>
  );
}
